'use client';

import React, { useState } from 'react';
import Icon from '@/components/ui/AppIcon';
import { type TopologyNode } from './topologyData';

interface TopologyNodeListProps {
  nodes: TopologyNode[];
  selectedNode: TopologyNode | null;
  onSelectNode: (node: TopologyNode) => void;
}

const statusDot: Record<string, string> = {
  online:      'bg-green-400',
  offline:     'bg-red-400',
  degraded:    'bg-amber-400',
  maintenance: 'bg-blue-400',
};

const typeIcons: Record<string, string> = {
  firewall:    'ShieldCheckIcon',
  router:      'GlobeAltIcon',
  switch:      'CircleStackIcon',
  server:      'ServerIcon',
  workstation: 'ComputerDesktopIcon',
  ap:          'WifiIcon',
  printer:     'PrinterIcon',
  camera:      'VideoCameraIcon',
  unknown:     'QuestionMarkCircleIcon',
};

export default function TopologyNodeList({ nodes, selectedNode, onSelectNode }: TopologyNodeListProps) {
  const [search, setSearch] = useState('');

  const filtered = nodes.filter(n =>
    !search || n.label.toLowerCase().includes(search.toLowerCase()) || n.ip.includes(search)
  );

  const grouped = filtered.reduce((acc, node) => {
    const key = node.type;
    if (!acc[key]) acc[key] = [];
    acc[key].push(node);
    return acc;
  }, {} as Record<string, TopologyNode[]>);

  const typeOrder = ['firewall', 'router', 'switch', 'server', 'ap', 'workstation', 'unknown'];

  return (
    <div className="flex flex-col h-full">
      <div className="p-3 border-b border-border">
        <p className="text-sm font-bold text-secondary-color mb-2 uppercase tracking-wide">
          Nodi ({nodes.length})
        </p>
        <div className="relative">
          <Icon name="MagnifyingGlassIcon" size={15} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-color" />
          <input
            type="text"
            placeholder="Filtra nodi..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full bg-surface-elevated border border-border rounded-lg pl-8 pr-3 py-2 text-sm text-white placeholder-muted-color focus:outline-none focus:border-sky-500/50 transition-colors font-mono"
          />
        </div>
      </div>
      <div className="flex-1 overflow-y-auto scrollbar-thin py-2">
        {typeOrder.filter(t => grouped[t]?.length).map((type) => (
          <div key={`group-type-${type}`}>
            <p className="px-3 py-1.5 text-xs font-bold text-muted-color uppercase tracking-widest">
              {type === 'firewall' ? 'Firewall' : type === 'router' ? 'Router' : type === 'switch' ? 'Switch' : type === 'server' ? 'Server' : type === 'ap' ? 'Access Point' : type === 'workstation' ? 'Postazioni' : 'Sconosciuti'} ({grouped[type].length})
            </p>
            {grouped[type].map((node) => {
              const isSelected = selectedNode?.id === node.id;
              return (
                <button
                  key={node.id}
                  onClick={() => onSelectNode(node)}
                  className={`
                    w-full flex items-center gap-2.5 px-3 py-2 text-left transition-colors
                    ${isSelected ? 'bg-sky-500/10' : 'hover:bg-surface-elevated'}
                  `}
                >
                  <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${statusDot[node.status] || 'bg-zinc-400'}`} />
                  <Icon
                    name={typeIcons[node.type] as any || 'ServerIcon'}
                    size={13}
                    className={`flex-shrink-0 ${isSelected ? 'text-sky-400' : 'text-muted-color'}`}
                  />
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm font-mono truncate ${isSelected ? 'text-sky-400' : 'text-secondary-color'}`}>
                      {node.label}
                    </p>
                    <p className="text-xs text-muted-color font-mono truncate">{node.ip}</p>
                  </div>
                  <span className={`text-xs font-mono flex-shrink-0 ${
                    node.vlan === 'VLAN-10' ? 'text-sky-500' :
                    node.vlan === 'VLAN-20' ? 'text-purple-400' :
                    node.vlan === 'VLAN-30' ? 'text-emerald-400' : 'text-muted-color'
                  }`}>
                    {node.vlan}
                  </span>
                </button>
              );
            })}
          </div>
        ))}
        {filtered.length === 0 && (
          <div className="flex flex-col items-center gap-2 py-8 px-4 text-center">
            <Icon name="MagnifyingGlassIcon" size={26} className="text-muted-color" />
            <p className="text-sm text-muted-color font-semibold">Nessun nodo trovato</p>
          </div>
        )}
      </div>
    </div>
  );
}